import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Box, Button, CircularProgress, Grid2 as Grid, Paper, Stack, Typography,
} from '@mui/material';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import {
  fetchMetaForecast, generateMetaForecastCommentary, type MetaSeriesForecast,
} from '../../api/meta';
import { MetricChart } from '../../components/MetricChart';
import { formatDateTime } from '../../components/formatters';
import type { AreaMetric } from '../../api/structure';

// The forecast reuses MetricChart's actual/plan convention: history renders as
// the solid Actual line, the forecast as the dashed line labeled "Prognoza".
function toAreaMetric(name: string, series: MetaSeriesForecast): AreaMetric {
  return {
    metric: name,
    subarea: series.seasonal ? 'trend + sezonowość' : 'sam trend (mało historii)',
    source: series.backtestMapePct !== null ? `backtest MAPE ${series.backtestMapePct}%` : '',
    unit: 'currency',
    format: 'currency',
    hasPlan: true,
    hasGrowthPct: false,
    monthToDateActual: false, // the forecast's history stops at the last completed month
    series: [
      ...series.history.map((p) => ({ month: p.month, actual: p.value, plan: null, growthPct: null })),
      ...series.forecast.map((p) => ({ month: p.month, actual: null, plan: p.value, growthPct: null })),
    ],
  };
}

export function MetaForecastPanel() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['meta-forecast'],
    queryFn: ({ signal }) => fetchMetaForecast(signal),
  });
  const mutation = useMutation({
    mutationFn: generateMetaForecastCommentary,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['meta-forecast'] }),
  });

  const data = query.data;
  const commentary = data?.commentary?.status === 'ok' ? data.commentary.payload : null;

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6">Prognoza kosztów (6 miesięcy)</Typography>
          <Typography variant="body2" color="text.secondary">
            Prognoza kierunkowa: trend + sezonowość miesięczna na danych z {data?.forecast.monthsUsed ?? '…'} pełnych miesięcy — nie przewiduje dokładnych cen aukcji
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeRoundedIcon />}
          disabled={mutation.isPending || !data?.forecast.cpm}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? 'Analizuję…' : 'Komentarz AI'}
        </Button>
      </Stack>

      {query.isLoading && (
        <Box sx={{ py: 4, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>
      )}

      {data && !data.forecast.cpm && (
        <Alert severity="info">Za mało pełnych miesięcy historii, żeby prognozować (potrzeba min. 6).</Alert>
      )}

      {data?.forecast.cpm && (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, lg: 4 }}>
            <MetricChart metric={toAreaMetric('CPM (PLN)', data.forecast.cpm)} planLabel="Prognoza" height={260} />
          </Grid>
          {data.forecast.cpc && (
            <Grid size={{ xs: 12, lg: 4 }}>
              <MetricChart metric={toAreaMetric('CPC za klik w link (PLN)', data.forecast.cpc)} planLabel="Prognoza" height={260} />
            </Grid>
          )}
          {data.forecast.spend && (
            <Grid size={{ xs: 12, lg: 4 }}>
              <MetricChart metric={toAreaMetric('Spend miesięczny (PLN)', data.forecast.spend)} planLabel="Prognoza" height={260} />
            </Grid>
          )}
        </Grid>
      )}

      {data?.commentary?.status === 'unavailable' && (
        <Alert severity="warning" sx={{ mt: 2 }}>Komentarz niedostępny: {data.commentary.statusReason}</Alert>
      )}

      {commentary && (
        <Box sx={{ mt: 2.5 }}>
          <Typography variant="caption" color="text.secondary">
            Komentarz AI · {formatDateTime(data!.commentary!.generatedAt)}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>{commentary.commentary}</Typography>
          {commentary.recommendations.length > 0 && (
            <Stack component="ul" spacing={0.5} sx={{ pl: 3, my: 1 }}>
              {commentary.recommendations.map((r, i) => (
                <Typography key={i} component="li" variant="body2">{r}</Typography>
              ))}
            </Stack>
          )}
        </Box>
      )}
    </Paper>
  );
}
